/**
 * Company & RBAC types — multi-tenant access control.
 */

export type CompanyRole = 'admin' | 'approver' | 'reviewer' | 'analyst' | 'viewer';

/**
 * Coarse-grained customer classification used by the report-generation
 * AI to frame conclusions in language the reader expects. A hyperscaler
 * doesn't want developer jargon; an EPC firm doesn't want investor
 * jargon. Drives the `readerLens` text injected into prompts and a
 * handful of UI defaults (risk tolerance, suggested report mix).
 *
 * Optional on Company for back-compat with legacy records — anything
 * undefined falls through to neutral developer/owner framing
 * (COMPANY_ARCHETYPES.other.readerLens).
 */
export type CompanyArchetype =
  | 'developer'              // pure-play data-center developer
  | 'hyperscaler'            // AWS/Azure/GCP/Meta-class self-build
  | 'colocation'             // Equinix/Digital Realty-class colo provider
  | 'utility'                // regulated electric utility
  | 'ipp'                    // independent power producer
  | 'energy-major'           // integrated O&G or generation major
  | 'sovereign'              // gov / national lab / military
  | 'consultancy'            // advisor preparing reports for clients
  | 'financial-sponsor'      // PE / infra fund / family office
  | 'owner-operator'         // hospital, university, industrial campus
  | 'epc'                    // engineering-procurement-construction firm
  | 'oem'                    // reactor or equipment vendor
  | 'research'               // academic or research institution
  | 'other';

export interface CompanyArchetypeMeta {
  id: CompanyArchetype;
  /** Long label shown on the create-company form + admin chips. */
  label: string;
  /** ≤6-char compact label for badges + tight grid cells. */
  shortLabel: string;
  /** 1-2 sentence positioning copy shown next to the label. */
  description: string;
  /**
   * The framing the report-generation prompt should adopt for this
   * archetype. Injected verbatim into the system prompt — keep it
   * directive (verbs, not nouns) and free of company-specific names
   * so it generalises across tenants.
   */
  readerLens: string;
  /** Default decision-making risk profile. UI uses this to seed the
   *  scoring-threshold customisation before the user overrides. */
  defaultRiskTolerance: 'low' | 'medium' | 'high';
  /** 0-3 short bullets describing the decisions this archetype is
   *  weighing. Surfaced on the create-company form to confirm the
   *  user picked the right archetype. */
  typicalDecisions: string[];
  /**
   * RC2 — 2-3 short, concrete narrative shifts this archetype
   * produces vs. neutral framing. Surfaced by `ArchetypeImpactDisclosure`
   * so the user can see *what changes about their report* when this
   * archetype is effective, without having to read the prompt source.
   *
   * Style guide: imperative or second-person, prose. No company names.
   * Each bullet should be specific enough that the user can verify it
   * in the produced report ("skips developer permit discussion",
   * "frames LCOE against fleet PPA benchmarks", etc.).
   */
  narrativeShifts: string[];
}

export const COMPANY_ARCHETYPES: Record<CompanyArchetype, CompanyArchetypeMeta> = {
  developer: {
    id: 'developer',
    label: 'Developer',
    shortLabel: 'Dev',
    description: 'Pure-play data-center developer assembling sites for sale, lease, or self-operation.',
    readerLens: 'Frame conclusions around developability, time-to-permit, and exit value. Highlight entitlement risk, construction sequencing, and tenant fit.',
    defaultRiskTolerance: 'medium',
    typicalDecisions: ['Site acquisition go/no-go', 'Sequence vs. parallelize permitting', 'Pre-leasing vs. spec build'],
    narrativeShifts: [
      'Emphasises time-to-permit and entitlement risk over plant operations detail',
      'Frames LCOE inside an exit-value story rather than a long-hold IRR',
      'Calls out tenant-fit and pre-lease optionality on every recommendation',
    ],
  },
  hyperscaler: {
    id: 'hyperscaler',
    label: 'Hyperscaler',
    shortLabel: 'Hyper',
    description: 'Self-build mega-tenant — AWS, Azure, GCP, Meta, Oracle.',
    readerLens: 'Frame around portfolio fit, redundancy across regions, time-to-power, and PPA optionality. Skip generic developer concerns; assume in-house EPC and capital.',
    defaultRiskTolerance: 'low',
    typicalDecisions: ['Region expansion go/no-go', 'PPA vs. self-generation', 'Cooling architecture fleet standardization'],
    narrativeShifts: [
      'Skips developer-tier permit discussion — assumes in-house land + EPC',
      'Frames LCOE against fleet PPA benchmarks, not merchant prices',
      'Heightens fleet redundancy / region-pair language; downplays single-site uniqueness',
    ],
  },
  colocation: {
    id: 'colocation',
    label: 'Colocation provider',
    shortLabel: 'Colo',
    description: 'Multi-tenant colocation operator — Equinix, Digital Realty, CyrusOne.',
    readerLens: 'Frame around carrier diversity, IX proximity, multi-tenant power redundancy, and SLA risk. Time-to-market matters more than absolute lowest cost.',
    defaultRiskTolerance: 'medium',
    typicalDecisions: ['Market entry vs. expansion', 'Anchor-tenant pre-lease', 'Power topology N+1 vs 2N'],
    narrativeShifts: [
      'Leads on carrier diversity and IX proximity before generation cost',
      'Discusses redundancy as N+1 vs 2N rather than abstract reliability',
      'Treats anchor-tenant pre-lease economics as a first-order risk lever',
    ],
  },
  utility: {
    id: 'utility',
    label: 'Electric utility',
    shortLabel: 'Util',
    description: 'Regulated electric utility evaluating large-load interconnection or its own generation.',
    readerLens: 'Frame around interconnection studies, capacity allocation, ratepayer impact, and PUC filings. Use IEEE/IEC terminology, not developer jargon.',
    defaultRiskTolerance: 'low',
    typicalDecisions: ['IRP supply election', 'Substation expansion timing', 'Large-load tariff design'],
    narrativeShifts: [
      'Uses IEEE/IEC terminology in place of developer-side jargon',
      'Discusses ratepayer impact and PUC filings as report-shaping constraints',
      'Anchors capacity discussion in IRP cycles rather than spot market dynamics',
    ],
  },
  ipp: {
    id: 'ipp',
    label: 'Independent power producer',
    shortLabel: 'IPP',
    description: 'Merchant generator developing assets for energy/capacity markets.',
    readerLens: 'Frame around merchant economics, basis risk, capacity market eligibility, and ISO interconnection queue position.',
    defaultRiskTolerance: 'medium',
    typicalDecisions: ['Project finance vs. balance-sheet', 'PPA vs. merchant', 'Queue cluster strategy'],
    narrativeShifts: [
      'Centres LMP basis risk and capacity-market eligibility in the financial case',
      'Treats interconnection queue position as a primary schedule driver',
      'Compares project finance and balance-sheet structures explicitly',
    ],
  },
  'energy-major': {
    id: 'energy-major',
    label: 'Energy major',
    shortLabel: 'Major',
    description: 'Integrated O&G major or vertically integrated power-and-gas company.',
    readerLens: 'Frame against existing portfolio synergies (gas, transport, real estate). Highlight optionality with hydrogen, CCS, and SMR partnerships.',
    defaultRiskTolerance: 'medium',
    typicalDecisions: ['Adjacent-asset reuse', 'Carbon intensity portfolio fit', 'JV vs. self-develop'],
    narrativeShifts: [
      'Cross-references adjacent gas / transport / real-estate assets where present',
      'Surfaces hydrogen, CCS, and SMR optionality alongside the base case',
      'Reports carbon-intensity in portfolio terms, not just project terms',
    ],
  },
  sovereign: {
    id: 'sovereign',
    label: 'Sovereign / Government',
    shortLabel: 'Sov',
    description: 'Government, national lab, military, or sovereign program.',
    readerLens: 'Frame around procurement compliance, security, and resilience. Cite federal regulations and standard contract vehicles.',
    defaultRiskTolerance: 'low',
    typicalDecisions: ['Mission assurance', 'Procurement vehicle selection', 'Public siting acceptance'],
    narrativeShifts: [
      'Cites federal regulations and named contract vehicles instead of generic procurement language',
      'Elevates mission assurance and security posture to first-tier risks',
      'Discusses public siting acceptance as an explicit work-stream',
    ],
  },
  consultancy: {
    id: 'consultancy',
    label: 'Consultancy / Advisor',
    shortLabel: 'Cons',
    description: 'Advisor preparing analysis for an undisclosed end client.',
    readerLens: 'Stay neutral. Present 2-3 framings rather than one recommendation. Include sensitivity ranges and counter-arguments. Never assume capital structure or operator identity.',
    defaultRiskTolerance: 'medium',
    typicalDecisions: ['Client recommendation framing', 'Scenario range', 'Risk register completeness'],
    narrativeShifts: [
      'Presents 2-3 framings instead of a single recommendation',
      'Always includes counter-arguments and sensitivity ranges',
      'Avoids assumptions about capital structure or operator identity',
    ],
  },
  'financial-sponsor': {
    id: 'financial-sponsor',
    label: 'Financial sponsor',
    shortLabel: 'Fin',
    description: 'PE, infrastructure fund, family office, or sovereign wealth fund.',
    readerLens: 'Frame around bankability, exit, IRR sensitivity, contracted-vs-merchant revenue mix, and downside protection.',
    defaultRiskTolerance: 'low',
    typicalDecisions: ['Underwriting case', 'Operator selection', 'Hold period exit assumptions'],
    narrativeShifts: [
      'Foregrounds bankability and IRR sensitivity over engineering elegance',
      'Splits revenue exposition into contracted vs. merchant mix',
      'Surfaces downside-protection levers and hold-period exit assumptions',
    ],
  },
  'owner-operator': {
    id: 'owner-operator',
    label: 'Owner-operator',
    shortLabel: 'O&O',
    description: 'Hospital, university, industrial campus, or other end-use organisation siting power for its own operations.',
    readerLens: 'Frame around operational continuity, on-site vs. grid trade-offs, total cost of energy, and resilience. Avoid investor jargon.',
    defaultRiskTolerance: 'low',
    typicalDecisions: ['On-site generation vs. grid', 'Resilience tier', 'Long-term operating cost'],
    narrativeShifts: [
      'Compares on-site generation against grid economics in plain language',
      'Reports total cost of energy and resilience tier, not investor metrics',
      'Discusses long-term operating cost over hold-period IRR',
    ],
  },
  epc: {
    id: 'epc',
    label: 'EPC firm',
    shortLabel: 'EPC',
    description: 'Engineering-procurement-construction firm bidding or executing the build.',
    readerLens: 'Frame around constructability, schedule risk, supply-chain lead times, and labor availability. Highlight specific scope items needing clarification.',
    defaultRiskTolerance: 'low',
    typicalDecisions: ['Bid/no-bid', 'Subcontractor mix', 'Schedule float'],
    narrativeShifts: [
      'Calls out constructability blockers and unclear scope explicitly',
      'Discusses supply-chain lead times and labour availability as schedule risks',
      'Frames recommendations as bid/no-bid posture and required clarifications',
    ],
  },
  oem: {
    id: 'oem',
    label: 'OEM',
    shortLabel: 'OEM',
    description: 'Reactor or equipment vendor evaluating the site for its product fit.',
    readerLens: 'Frame around product fit (footprint, water, seismic), reference-plant precedents, and licensing pathway alignment with the OEM design certification.',
    defaultRiskTolerance: 'medium',
    typicalDecisions: ['Product offering selection', 'Co-marketing with developer', 'Engineering deliverable scope'],
    narrativeShifts: [
      'Tests footprint, water, and seismic data against the OEM product envelope',
      'Cites reference-plant precedents that match the design certification',
      'Frames the deliverable scope around licensing pathway alignment',
    ],
  },
  research: {
    id: 'research',
    label: 'Research institution',
    shortLabel: 'Res',
    description: 'Academic group, national lab, or research consortium.',
    readerLens: 'Frame around scientific question, dataset transparency, and reproducibility. Cite primary sources liberally.',
    defaultRiskTolerance: 'high',
    typicalDecisions: ['Study scope', 'Publication framing', 'Dataset sharing'],
    narrativeShifts: [
      'Foregrounds the scientific question and reproducibility constraints',
      'Cites primary sources liberally rather than aggregated industry briefs',
      'Treats dataset transparency and sharing constraints as deliverables',
    ],
  },
  other: {
    id: 'other',
    label: 'Other',
    shortLabel: 'Other',
    description: 'Profile not listed above.',
    readerLens: 'Use neutral developer/owner framing. Ask the user to refine the archetype if reports feel off-target.',
    defaultRiskTolerance: 'medium',
    typicalDecisions: [],
    narrativeShifts: [
      'Uses neutral developer/owner framing as a sensible default',
      'Prompts the user to refine the archetype if reports feel off-target',
    ],
  },
};

export interface Company {
  id: string;
  name: string;
  /** Parent organization id for hierarchy trees; null/undefined means root. */
  parentCompanyId?: string | null;
  /** Root-to-self ancestry path for fast subtree checks. */
  path?: string[];
  /**
   * Coarse customer classification driving the AI prompt's reader lens.
   * Optional — undefined on legacy records, falls through to neutral
   * developer/owner framing in COMPANY_ARCHETYPES.other.readerLens.
   */
  archetype?: CompanyArchetype;
  /** Optional one-liner clarification, e.g. "developer, focused on
   *  edge sites" — appended to the reader lens when present. */
  archetypeNote?: string;
  features: {
    siteRag: boolean;
    simulation: boolean;
  };
  settings: {
    defaultRole: CompanyRole;
    approvalRequired: boolean;
    /**
     * Documents_On_Site Prompt 9 — overrides the default missing-critical
     * confidence cap (0.75). Tenant admin can dial this down to be
     * stricter (e.g. 0.5) or up to be more permissive (1.0 = no cap).
     * Optional: undefined → use DEFAULT_MISSING_CRITICAL_CAP.
     */
    missingCriticalCap?: number;
    /**
     * SA pack SA2 — when true, SED amendments stay in `status: 'pending'`
     * after `saveSEDAmendment` until a reviewer approves them through
     * the existing `updateAmendmentStatus` → `createSEDRevision` flow.
     * When false / undefined (the default), amendments take effect on
     * the live SED immediately ("applied" status) and the user's single
     * click both records the amendment AND mutates the SED.
     *
     * Solo users and small teams get the snappy direct-apply path;
     * regulated orgs with explicit review policy flip this on.
     */
    requireReviewWorkflow?: boolean;
    /**
     * R-32 — per-tenant override for the SED bootstrap freshness gate
     * (ms). When unset, `sedBootstrapCoordinator` defaults to 5 minutes.
     */
    sedBootstrapFreshnessThresholdMs?: number;
  };
  createdAt: string;
  createdBy: string;
}

export interface CompanyMember {
  companyId: string;
  uid: string;
  email: string;
  displayName: string;
  companyRole: CompanyRole;
  features: {
    canAccessSiteRag: boolean;
    canAccessSimulation: boolean;
  };
  joinedAt: string;
  invitedBy: string;
  updatedAt?: string;
}

export interface FeatureAccess {
  canAccessSiteRag: boolean;
  canAccessSimulation: boolean;
}

/** Permission check helpers based on company role. */
export const ROLE_PERMISSIONS: Record<CompanyRole, {
  canView: boolean;
  canViewCompany: boolean;
  canCreate: boolean;
  canReview: boolean;
  canApprove: boolean;
  canDelete: boolean;
  canManageUsers: boolean;
}> = {
  viewer:   { canView: true, canViewCompany: true, canCreate: false, canReview: false, canApprove: false, canDelete: false, canManageUsers: false },
  analyst:  { canView: true, canViewCompany: true, canCreate: true,  canReview: false, canApprove: false, canDelete: false, canManageUsers: false },
  reviewer: { canView: true, canViewCompany: true, canCreate: true,  canReview: true,  canApprove: false, canDelete: false, canManageUsers: false },
  approver: { canView: true, canViewCompany: true, canCreate: true,  canReview: true,  canApprove: true,  canDelete: false, canManageUsers: false },
  admin:    { canView: true, canViewCompany: true, canCreate: true,  canReview: true,  canApprove: true,  canDelete: true,  canManageUsers: true },
};
